import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/Field";
import { FeesInfo } from "@/components/FeesInfo";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const empty = { name: "", email: "", phone: "", organization: "", message: "" };

export default function Contact() {
  const { L } = useLang();
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/contact", f);
      toast.success(L("Message sent! We'll get back to you shortly.", "संदेश भेज दिया गया! हम जल्द ही आपसे संपर्क करेंगे।"));
      setF(empty);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || L("Could not send message.", "संदेश नहीं भेजा जा सका।"));
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#E8F0FE]" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("Contact", "संपर्क")}</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0A192F] mt-3 leading-tight">{L("Let's build something together.", "आइए, मिलकर कुछ बनाएँ।")}</h1>
          <p className="text-slate-600 mt-4">{L("Schools, parents, students, buyers and partners — we'd love to hear from you.", "स्कूल, अभिभावक, छात्र, ख़रीदार और साझेदार — हमें आपसे सुनकर खुशी होगी।")}</p>
          <div className="mt-10 space-y-5">
            {[{ i: MapPin, t: L("Head Office", "मुख्य कार्यालय"), d: L("RoboSpark PrP Pvt Ltd, C-30, C-Block, Sector 63, Noida-201301 (U.P.)", "RoboSpark PrP Pvt Ltd, C-30, C-ब्लॉक, सेक्टर 63, नोएडा-201301 (उ.प्र.)") }, { i: Mail, t: L("Email", "ईमेल"), d: "robospark.prp@gmail.com" }, { i: Phone, id: "call", t: L("Call", "कॉल करें"), d: "9608321115", href: "tel:+919608321115" }, { i: MessageCircle, id: "whatsapp", t: "WhatsApp", d: "8100499334", href: "https://wa.me/918100499334" }].map((x) => (
              <div key={x.t} className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl bg-[#F0F4F8] grid place-items-center"><x.i className="w-5 h-5 text-[#007A8A]" /></div>
                <div><div className="font-bold text-[#0A192F]">{x.t}</div>{x.href ? <a href={x.href} target={x.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-[#FF2E63] text-lg font-black font-['Outfit'] hover:underline" data-testid={`contact-${x.id}-link`}>{x.d}</a> : <div className="text-slate-600 text-sm">{x.d}</div>}</div>
              </div>
            ))}
          </div>
          <div className="mt-8"><FeesInfo testid="contact-fees" /></div>
          <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 h-56">
            <iframe title="map" className="w-full h-full" src="https://maps.google.com/maps?q=C-30+Sector+63+Noida+201301&z=15&output=embed" loading="lazy" />
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 bg-white p-6 md:p-10 rounded-2xl border border-slate-200 grid sm:grid-cols-2 gap-5 h-fit" data-testid="contact-form">
          <Field id="c_name" label={L("Name", "नाम")} required value={f.name} onChange={on("name")} />
          <Field id="c_email" label={L("Email", "ईमेल")} type="email" required value={f.email} onChange={on("email")} />
          <Field id="c_phone" label={L("Phone", "फ़ोन")} value={f.phone} onChange={on("phone")} />
          <Field id="c_org" label={L("School / Organisation", "स्कूल / संस्था")} value={f.organization} onChange={on("organization")} />
          <div className="sm:col-span-2 space-y-1.5">
            <label htmlFor="c_message" className="text-sm font-semibold text-[#0A192F]">{L("Message", "संदेश")}<span className="text-[#FF2E63]"> *</span></label>
            <Textarea id="c_message" required rows={6} value={f.message} onChange={on("message")} data-testid="input-c_message" />
          </div>
          <Button type="submit" disabled={loading} className="sm:col-span-2 rs-cta h-12 rounded-full" data-testid="contact-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Send Message", "संदेश भेजें")}
          </Button>
        </form>
      </div>
    </div>
  );
}
